import apiClient from './client';
import { ApiResponse, PaginatedResponse } from '../types/api.types';

export interface AdminUser {
  _id: string;
  email?: string;
  username?: string;
  name: string;
  role: string;
  isActive?: boolean;
  permissions?: string[];
}

export interface ApiKey {
  name: string;
  key?: string;
  createdAt?: string;
  expiresAt?: string | null;
  lastUsedAt?: string | null;
}

export const adminApi = {
  login: (username: string, password: string): Promise<{
    data: { accessToken: string; refreshToken: string; user: AdminUser };
  }> => apiClient.post('/api/v1/user-admin/login', { username, password }),

  refresh: (refreshToken: string): Promise<{ data: { accessToken: string } }> =>
    apiClient.post('/api/v1/user-admin/refresh', { refreshToken }),

  logout: (): Promise<ApiResponse<null>> => apiClient.post('/api/v1/user-admin/logout'),

  profile: (): Promise<{ data: AdminUser }> => apiClient.get('/api/v1/user-admin/profile'),

  listAdmins: async (page = 1, limit = 20): Promise<ApiResponse<PaginatedResponse<AdminUser>>> => {
    const res = await apiClient.get<ApiResponse<any>>(`/api/v1/user-admin/admins?page=${page}&limit=${limit}`);
    const d = res.data || {};

    const admins: AdminUser[] = d.items ?? d.admins ?? [];
    const total: number =
      d.total ?? d.totalItems ?? d.count ?? (Array.isArray(admins) ? admins.length : 0);
    const pageNum: number = d.page ?? d.currentPage ?? page ?? 1;
    const limitNum: number = d.limit ?? limit ?? 20;
    const totalPages: number =
      d.totalPages ?? d.pages ?? (limitNum > 0 ? Math.ceil(total / limitNum) : 1);

    return {
      ...res,
      data: {
        items: admins,
        total,
        page: pageNum,
        limit: limitNum,
        totalPages,
      },
    };
  },

  getAdminsStats: (): Promise<ApiResponse<any>> =>
    apiClient.get('/api/v1/user-admin/admins/stats'),

  getAdmin: (adminId: string): Promise<ApiResponse<AdminUser>> =>
    apiClient.get(`/api/v1/user-admin/admins/${adminId}`),

  createAdmin: (payload: {
    username: string;
    password: string;
    name: string;
    role: string;
  }): Promise<ApiResponse<AdminUser>> =>
    apiClient.post('/api/v1/user-admin/admins', payload),

  updateAdmin: (adminId: string, payload: Partial<AdminUser> & { username?: string }): Promise<ApiResponse<AdminUser>> =>
    apiClient.put(`/api/v1/user-admin/admins/${adminId}`, payload),

  deleteAdmin: (adminId: string): Promise<ApiResponse<null>> =>
    apiClient.delete(`/api/v1/user-admin/admins/${adminId}`),

  updatePermissions: (adminId: string, permissions: string[]): Promise<ApiResponse<AdminUser>> =>
    apiClient.put(`/api/v1/user-admin/admins/${adminId}/permissions`, { permissions }),

  getApiKeys: (): Promise<ApiResponse<ApiKey[]>> =>
    apiClient.get('/api/v1/user-admin/api-keys'),

  createApiKey: (payload: { name: string; expiresInDays?: number }): Promise<ApiResponse<ApiKey>> =>
    apiClient.post('/api/v1/user-admin/api-keys', payload),

  deleteApiKey: (keyName: string): Promise<ApiResponse<null>> =>
    apiClient.delete(`/api/v1/user-admin/api-keys/${encodeURIComponent(keyName)}`),
};